import React, { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import type { User } from '../types';
import { getApiUrl } from '../utils/api';

type LoginPayload =
  | { mobile_number: string; mobile_country: string; password: string; legacy?: false }
  | { email: string; password: string; legacy: true };

interface LoginResult {
  requiresMobileBinding: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  pendingMobileBinding: boolean;
  login: (payload: LoginPayload) => Promise<LoginResult>;
  signup: (mobileNumber: string, mobileCountry: string, password: string, name: string) => Promise<void>;
  completeMobileBinding: (newToken: string, updatedUser: User) => void;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingMobileBinding, setPendingMobileBinding] = useState(false);
  const hasHadUserRef = useRef(false); // Track if user was ever logged in

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          // Verify token with backend
          const response = await fetch(getApiUrl('AUTH_VERIFY'), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const { data } = await response.json();
            setUser(data);
            setPendingMobileBinding(data.mobile_bound === false);
            hasHadUserRef.current = true;
          } else {
            // Token is invalid, remove it
            localStorage.removeItem('authToken');
            setUser(null);
            setPendingMobileBinding(false);
          }
        }
      } catch (err) {
        console.error('Auth check error:', err);
        localStorage.removeItem('authToken');
        setUser(null);
        setPendingMobileBinding(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Listen for storage changes (e.g., when token is removed by API interceptor)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken' && e.newValue === null && user !== null) {
        // Token was removed externally (e.g., by API interceptor)
        // Don't reset hasHadUserRef here - let redirect useEffect handle it
        setUser(null);
        setPendingMobileBinding(false);
      }
    };

    // Also listen for custom logout event from API interceptor
    const handleLogoutEvent = () => {
      if (user !== null) {
        // Don't reset hasHadUserRef here - let redirect useEffect handle it
        setUser(null);
        setPendingMobileBinding(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-logout', handleLogoutEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-logout', handleLogoutEvent);
    };
  }, [user]);

  // Redirect to landing page when user logs out (user becomes null after initial load)
  useEffect(() => {
    // Only redirect if:
    // 1. Initial loading is complete (isLoading === false)
    // 2. User is null (logged out)
    // 3. User was previously logged in (hasHadUserRef.current === true)
    // 4. Token is missing from localStorage (confirming logout happened)
    // 5. We're not already on the landing page or admin pages
    if (
      !isLoading && 
      user === null && 
      hasHadUserRef.current &&
      !localStorage.getItem('authToken') &&
      window.location.pathname !== '/' &&
      !window.location.pathname.startsWith('/admin')
    ) {
      // Reset the ref before redirecting to prevent infinite loops
      hasHadUserRef.current = false;
      window.location.href = '/';
    }
  }, [user, isLoading]);

  const login = async (payload: LoginPayload): Promise<LoginResult> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(getApiUrl('AUTH_LOGIN'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token and set user
      localStorage.setItem('authToken', data.token);
      setUser(data.user);
      const requiresMobileBinding = data?.data?.requires_mobile_binding === true || data.user?.mobile_bound === false;
      setPendingMobileBinding(requiresMobileBinding);
      hasHadUserRef.current = true;
      return { requiresMobileBinding };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (mobileNumber: string, mobileCountry: string, password: string, name: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(getApiUrl('AUTH_SIGNUP'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobile_number: mobileNumber, mobile_country: mobileCountry, password, name }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Store token and set user
      localStorage.setItem('authToken', data.token);
      setUser(data.user);
      setPendingMobileBinding(data.user?.mobile_bound === false);
      hasHadUserRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const completeMobileBinding = (newToken: string, updatedUser: User) => {
    localStorage.setItem('authToken', newToken);
    setUser(updatedUser);
    setPendingMobileBinding(false);
    setError(null);
    hasHadUserRef.current = true;
  };

  const logout = async () => {
    try {
      localStorage.removeItem('authToken');
      hasHadUserRef.current = false; // Reset the ref
      setUser(null);
      setPendingMobileBinding(false);
      // Redirect to landing page after logout
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    error,
    pendingMobileBinding,
    login,
    signup,
    completeMobileBinding,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
